import { BaseSecurityModule } from '../base.security.module';
import { SecurityModuleName, SecurityRight } from '../security.module';

export enum JobSecurityRight {
    CanAccess = 1000,
    Manage = 1001,
    Dispatch = 1002,
    FinaliseJob = 1003,
    FinalliseWorkOrder = 1004,
    CompletionDate = 1005,
    FinaliseDate = 1006,
    ProcessJob = 1007,
    ProcessWorkOrder = 1008,
    ViewPricing = 1009,
    ModifyPricing = 1010,
    ManageActivity = 1011,
    ManageOtherUserActivity = 1012,
    ManagePurchaseOrder = 1013,
    ManageInvoices = 1014,
    ManageBills = 1015,
    MangeOtherUserViews = 1016,
    ModifyJobAfterFinalise = 1017,
    ReverseWorkOrderStatus = 1018,
    CanCreateViews = 1019,
    CanAccessSystemViews = 1020,
    ManageQuote = 1021,
    AccessQuote = 1022,
    ModifyLockedItems = 1023,
    ManageSalesOrder = 1024,
    OverrideCapacityRestriction = 1027,
    AllowOutOfZoneSelection = 1026,
    ViewAuditLog = 1025,
    MarkFinanceLock = 1028,
    ApprovePendingItems = 1029,
    ChangeJobStructure = 1030,
    TasksView = 1031,
    TasksCreate = 1032,
    TasksEdit = 1033,
    TasksDelete = 1034,
    ItemsView = 1035,
    ItemsCreate = 1036,
    ItemsEdit = 1037,
    ItemsDelete = 1038,
    SorsView = 1039,
    SorsCreate = 1040,
    SorsEdit = 1041,
    SorsDelete = 1042,
    CompleteStagesWithUnresolvedTasks = 1043,
}

export class JobSecurityModule extends BaseSecurityModule<JobSecurityRight> {

    constructor() {
        super(
            SecurityModuleName.Jobs,
            [
                { _id: JobSecurityRight.CanAccess, name: 'Access', basic: true },
                { _id: JobSecurityRight.Manage, name: 'Manage', basic: true },
                { _id: JobSecurityRight.Dispatch, name: 'Dispatch Stage', basic: false },
                { _id: JobSecurityRight.FinaliseJob, name: 'Finalise Job', basic: false },
                { _id: JobSecurityRight.FinalliseWorkOrder, name: 'Finalise Stage', basic: false },
                { _id: JobSecurityRight.CompletionDate, name: 'Modify Completion Date after the fact', basic: false },
                { _id: JobSecurityRight.FinaliseDate, name: 'Modify Finalisation Date after the fact', basic: false },
                { _id: JobSecurityRight.ProcessJob, name: 'Modify Job Status', basic: false },
                { _id: JobSecurityRight.ProcessWorkOrder, name: 'Modify Stage Status', basic: false },
                { _id: JobSecurityRight.ViewPricing, name: 'View Sell & Buy Prices', basic: false },
                { _id: JobSecurityRight.ModifyPricing, name: 'Change Sell & Buy Prices on Items & SORs', basic: false },
                { _id: JobSecurityRight.ModifyJobAfterFinalise, name: 'Modify Job after Finalise', basic: false },
                { _id: JobSecurityRight.ModifyLockedItems, name: 'Modify Locked Items & SORs', basic: false },
                { _id: JobSecurityRight.MarkFinanceLock, name: 'Mark Job or Stage Non-Billable / Non-Payable', basic: false },
                // Approving a requested line commits money, so only the administrator holds it by default (WBS-12230).
                // Decline keeps the existing job rights.
                { _id: JobSecurityRight.ApprovePendingItems, name: 'Approve Pending Items & SORs', basic: false },
                // Only effective together with Manage: the name says so, because a role ticked for this
                // right alone would otherwise look able to change a job's structure and silently cannot.
                { _id: JobSecurityRight.ChangeJobStructure, name: 'Change Job Structure (requires Manage)', basic: false },
                { _id: JobSecurityRight.ManageActivity, name: 'Manage Activity', basic: false },
                { _id: JobSecurityRight.ManageOtherUserActivity, name: 'Manage Other User Activities', basic: false },
                { _id: JobSecurityRight.ManagePurchaseOrder, name: 'Manage Purchase Orders on Job', basic: false },
                { _id: JobSecurityRight.ManageInvoices, name: 'Manage Invoices on Job', basic: false },
                { _id: JobSecurityRight.ManageBills, name: 'Manage Bills on Stage', basic: false },
                { _id: JobSecurityRight.MangeOtherUserViews, name: 'Can Manage Other User Views', basic: false },
                { _id: JobSecurityRight.ReverseWorkOrderStatus, name: 'Revert Stage Status', basic: false },
                // Web App only: authorises completing a stage while Stage Completion tasks are open (WBS-12220).
                // The administrator holds it; every other seeded role excludes it so a tenant opts each one in.
                { _id: JobSecurityRight.CompleteStagesWithUnresolvedTasks, name: 'Complete stages with unresolved tasks', basic: false },
                { _id: JobSecurityRight.CanCreateViews, name: 'Can Create Views', basic: false },
                { _id: JobSecurityRight.CanAccessSystemViews, name: 'Can Access System Views', basic: false },
                { _id: JobSecurityRight.AccessQuote, name: 'Access Quotes on Jobs', basic: false },
                { _id: JobSecurityRight.ManageQuote, name: 'Manage Quotes on Jobs', basic: false },
                { _id: JobSecurityRight.ManageSalesOrder, name: 'Manage Sales Order on Jobs', basic: false },
                { _id: JobSecurityRight.OverrideCapacityRestriction, name: 'Contractor Recommendations > Allow capacity constrained selection', basic: false },
                { _id: JobSecurityRight.AllowOutOfZoneSelection, name: 'Contractor Recommendations > Allow out of zone selection', basic: false },
                { _id: JobSecurityRight.ViewAuditLog, name: 'View Audit Log', basic: false },
                // Section rights: each one adds a check on top of the job rights above and never replaces one.
                // Existing roles receive them once from the section-rights tenant upgrade; system roles derive
                // them in their class (`withDerivedJobSectionRights`).
                { _id: JobSecurityRight.TasksView, name: 'Tasks › View', basic: false },
                { _id: JobSecurityRight.TasksCreate, name: 'Tasks › Create', basic: false },
                { _id: JobSecurityRight.TasksEdit, name: 'Tasks › Edit', basic: false },
                { _id: JobSecurityRight.TasksDelete, name: 'Tasks › Delete', basic: false },
                { _id: JobSecurityRight.ItemsView, name: 'Items › View', basic: false },
                { _id: JobSecurityRight.ItemsCreate, name: 'Items › Create', basic: false },
                { _id: JobSecurityRight.ItemsEdit, name: 'Items › Edit', basic: false },
                { _id: JobSecurityRight.ItemsDelete, name: 'Items › Delete', basic: false },
                { _id: JobSecurityRight.SorsView, name: 'SORs › View', basic: false },
                { _id: JobSecurityRight.SorsCreate, name: 'SORs › Create', basic: false },
                { _id: JobSecurityRight.SorsEdit, name: 'SORs › Edit', basic: false },
                { _id: JobSecurityRight.SorsDelete, name: 'SORs › Delete', basic: false },
            ] as SecurityRight<JobSecurityRight>[]
        );
    }


}
